export type Totals = {
  saleValue: number;
  recordedReceipts: number;
  commission: number;
  deals: number;
};
export type Month = Totals & { key: string; month: number };
export type Report = {
  generatedAt: string;
  period: {
    year: number;
    type: string;
    start: string;
    end: string;
    previousStart: string;
    previousEnd: string;
  };
  years: number[];
  totals: Totals;
  accountsToReview: number;
  months: Month[];
  previous: { totals: Totals; months: Month[] };
  agents: (Totals & { id: string; name: string })[];
  stages: { stage: string; count: number }[];
  inventory: {
    id: string;
    name: string;
    total: number;
    available: number;
    reserved: number;
    sold: number;
    other: number;
  }[];
  overdue: {
    total: number;
    count: number;
    items: {
      id: string;
      client: string;
      lot: string;
      amount: number;
      daysOverdue: number;
    }[];
  };
};
export const stageLabels: Record<string, string> = {
  INQUIRY: "Inquiry",
  RESERVATION: "Reservation",
  CONTRACT_SIGNING: "Contract signing",
  PAYMENT: "Payment",
  COMPLETED: "Completed",
};
export const fmt = (n: number) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
export const shortMoney = (n: number) =>
  `₱${new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n)}`;
export const monthName = (month: number) =>
  new Date(Date.UTC(2020, month - 1, 1)).toLocaleDateString("en", {
    month: "short",
    timeZone: "UTC",
  });
export const dateLabel = (date: string) =>
  new Date(date + "T00:00:00Z").toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
export function changeLabel(current: number, previous: number) {
  if (!previous)
    return current ? "No prior-year baseline" : "No activity in either period";
  const change = ((current - previous) / previous) * 100;
  return `${change > 0 ? "+" : ""}${change.toFixed(1)}% vs prior year`;
}
export function csvCell(value: string | number) {
  // Spreadsheet formula prefixes remain text, including after whitespace/control characters.
  const text = String(value);
  const first = Array.from(text).find(
    (c) => !/\s/.test(c) && c.charCodeAt(0) > 31,
  );
  const safe =
    typeof value === "string" && first !== undefined && "=+-@".includes(first)
      ? "'" + text
      : text;
  return `"${safe.replace(/"/g, '""')}"`;
}
export function reportCSV(report: Report) {
  const rows: (string | number)[][] = [
    ["Scope", "Record", "Metric", "Value", "From", "Through", "Generated at"],
  ];
  const add = (
    scope: string,
    record: string,
    values: Record<string, number>,
    snapshot = false,
    previous = false,
  ) => {
    Object.entries(values).forEach(([metric, value]) =>
      rows.push([
        scope,
        record,
        metric,
        value,
        snapshot
          ? ""
          : previous
            ? report.period.previousStart
            : report.period.start,
        snapshot
          ? ""
          : previous
            ? report.period.previousEnd
            : report.period.end,
        report.generatedAt,
      ]),
    );
  };
  add("Selected deals", "Total", report.totals);
  add("Prior-year deals", "Total", report.previous.totals, false, true);
  report.previous.months.forEach((m) =>
    add(
      "Prior-year monthly cohort",
      m.key,
      {
        saleValue: m.saleValue,
        recordedReceipts: m.recordedReceipts,
        commission: m.commission,
        deals: m.deals,
      },
      false,
      true,
    ),
  );
  add("Data quality", "Accounts to review", { count: report.accountsToReview });
  report.months.forEach((m) =>
    add("Monthly deal cohort", m.key, {
      saleValue: m.saleValue,
      recordedReceipts: m.recordedReceipts,
      commission: m.commission,
      deals: m.deals,
    }),
  );
  report.agents.forEach((a) =>
    add("Assigned agent", a.name, {
      saleValue: a.saleValue,
      recordedReceipts: a.recordedReceipts,
      commission: a.commission,
      deals: a.deals,
    }),
  );
  report.stages.forEach((s) =>
    add("Current stage of selected deals", stageLabels[s.stage] || s.stage, {
      deals: s.count,
    }),
  );
  report.inventory.forEach((s) =>
    add(
      "Current inventory",
      s.name,
      {
        total: s.total,
        available: s.available,
        reserved: s.reserved,
        sold: s.sold,
        other: s.other,
      },
      true,
    ),
  );
  add(
    "Current overdue schedules",
    "All",
    { outstanding: report.overdue.total, schedules: report.overdue.count },
    true,
  );
  rows.push([
    "Definitions",
    "Selected deals",
    "Non-cancelled deals created in the period; includes inquiries. Receipts and commissions are current recorded amounts for those deals, not cash flow by receipt date.",
    "",
    "",
    "",
    report.generatedAt,
  ]);
  return "\ufeff" + rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}
